import { formatVolumeCap, volumeCapExportValue } from "utilities/volumeCap";
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { requireAuth, useAuth } from "utilities/auth";
import { isInternal, isPartner, isProcessor } from "utilities/userType";
import { useMids } from "hooks/crm/useMids";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import { MID_STATUS, statusToneFrom } from "utilities/statusTones";
import SearchPanel from "components/SearchPanel";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import SourceBadge from "components/SourceBadge";
import RelativeTime from "components/RelativeTime";
import RecordTable from "components/Records/RecordTable";
import RecordStatusPill from "components/Records/RecordStatusPill";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import { toCsv, csvFilename, downloadCsv } from "utilities/csv";
import {
  Filter,
  CreditCard,
  Plus,
  CheckCircle,
  DollarSign,
  MinusCircle,
  Download,
} from "lucide-react";

const midTone = statusToneFrom(MID_STATUS);
const MID_FILTER_FIELDS = filterFieldsFor("mids");

function MidPortfolioInner() {
  const { filters, set } = useSearch();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isInternal(user);
  const isPartnerUser = isPartner(user);
  // Processors (SOUND-563) get a read-only monitoring list: no Create MID
  // (already admin-gated), and rows don't drill into /mids/:id (that route is
  // not admitted for them — server default-deny). Keep the list itself.
  const isProcessorUser = isProcessor(user);

  const { mids, pagination, summary, loading, error, refetch, fetchAll } = useMids({
    filters,
    page,
    limit,
    sort,
  });

  const [exporting, setExporting] = useState(false);

  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const activeFilterCount = countActiveFilters(filters, MID_FILTER_FIELDS);

  // Column order is shared with /mid-applications: identity first, then the
  // common spine Processor | Source | Status, then page-specific, then Updated.
  // Only columns backed by a STORED field carry a sortKey — Client, DBA and
  // Source are resolved after the paginated query and Mongo cannot order by
  // them, so they render without a sort affordance rather than with one that
  // does nothing.
  const columns = useMemo(
    () => [
      {
        key: "client",
        width: "17%",
        header: "Client",
        exportValue: (m) => m.clientName,
        cell: (m) => <div className="truncate">{m.clientName || "—"}</div>,
      },
      {
        key: "name",
        width: "15%",
        header: "Name",
        sortKey: "name",
        cell: (m) => <div className="font-medium">{m.name || "—"}</div>,
      },
      {
        key: "mid",
        width: "15%",
        header: "MID",
        sortKey: "mid",
        // Tabular figures so the numbers align down the column and a wrong
        // digit-count is visible at a glance.
        className: "font-mono text-[11px] tabular-nums text-muted-foreground",
        // exportAs:"text" — MID numbers carry leading zeros (019100114637) and
        // Excel would silently drop them, breaking reconciliation.
        exportAs: "text",
        cell: (m) => m.mid || "—",
      },
      {
        key: "monthlyVolumeCap",
        width: "10%",
        header: "Volume cap",
        sortKey: "monthlyVolumeCap",
        align: "right",
        exportValue: volumeCapExportValue,
        cell: formatVolumeCap,
      },
      {
        key: "processorName",
        width: "11%",
        header: "Processor",
        sortKey: "processorName",
        cell: (m) => m.processorName || "—",
      },
      // SOUND-570: processors must never see which partner sourced a merchant.
      // One entry gates header AND cells together — they cannot desynchronise.
      // The API already omits `.source` for processors; this is defense in depth.
      !isProcessorUser && {
        key: "source",
        width: "12%",
        header: "Source",
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell",
        exportValue: (m) => m.source?.type || "",
        cell: (m) => <SourceBadge source={m.source} />,
      },
      {
        key: "status",
        width: "9%",
        header: "Status",
        sortKey: "status",
        className: "w-36",
        exportValue: (m) => MID_STATUS[m.status]?.label || m.status || "",
        cell: (m) => <RecordStatusPill status={m.status} toneFor={midTone} />,
      },
      {
        // "Updated" is a staleness signal, and staleness means nothing for a
        // MID — one untouched for 30 days is working as intended, unlike an
        // application. What matters is when it went live.
        //
        // NOT SORTABLE YET, deliberately. effectiveDate is the right field but
        // is null on all 265 MIDs (SOUND-642 made it capturable; the backfill is
        // ops keying with no derivable source). Sorting on it would do nothing;
        // sorting on createdAt would be correct today and silently wrong once
        // effectiveDate starts landing. Add sortKey:"effectiveDate" when 642 is
        // backfilled — same rule as Client/DBA/Source: no affordance unless the
        // server can order meaningfully.
        key: "effectiveDate",
        width: "11%",
        header: "Effective",
        headerClassName: "hidden lg:table-cell w-32",
        className: "hidden lg:table-cell text-muted-foreground",
        exportValue: (m) => m.effectiveDate || m.createdAt || "",
        cell: (m) =>
          m.effectiveDate ? (
            <RelativeTime value={m.effectiveDate} />
          ) : (
            // createdAt is when the record was KEYED, not when the MID went
            // live — many cluster on import dates. Marked as a stand-in rather
            // than dressed up as the real thing.
            <span
              className="opacity-60"
              title="No effective date recorded — showing when the MID was added to the CRM"
            >
              Added <RelativeTime value={m.createdAt} />
            </span>
          ),
      },
    ],
    [isProcessorUser]
  );

  // Exports the FILTERED result set, not the loaded page — the same reasoning
  // that put the KPI counts on the server (SOUND-295). Columns come from the
  // table model, so a processor's export omits Source exactly as their table does.
  const handleExport = async () => {
    setExporting(true);
    try {
      const { rows, truncated } = await fetchAll();
      downloadCsv(csvFilename("mids"), toCsv(rows, columns));
      if (truncated) {
        window.alert(
          `Export capped at ${rows.length.toLocaleString()} rows of ${pagination.total.toLocaleString()}. Narrow the filters and export again.`
        );
      }
    } catch {
      window.alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Portfolio"
        subtitle={
          isProcessorUser
            ? "Monitor the MIDs across the merchants you acquire."
            : "Manage active MIDs across your merchant portfolio."
        }
        meta={[{ label: "Total", value: (pagination.total || 0).toLocaleString() }]}
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="badge badge-brand ml-1">{activeFilterCount}</span>
              )}
            </button>
            {isAdmin && (
              <button className="btn btn-primary" onClick={() => navigate("/mids/create")}>
                <Plus className="h-4 w-4" />
                Create MID
              </button>
            )}
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        <PageKpis
          items={[
            {
              label: "Total MIDs",
              value: pagination.total || 0,
              tone: "brand",
              loading,
              icon: <CreditCard />,
            },
            {
              // SOUND-657: replaced Suspended, which is structurally always 0
              // (250 active, 6 closed, 0 suspended). Aggregated server-side over
              // the SAME filter as the page — a total computed from 25 visible
              // rows would be wrong and would change as you paged.
              label: "Total active cap",
              value: summary.activeCap?.total ?? 0,
              format: "currency",
              currency: "USD",
              compactValue: true,
              tone: "brand",
              loading,
              icon: <DollarSign />,
              // An uncapped MID has no number to add, so the total EXCLUDES it.
              // Saying so is the difference between an understated figure and a
              // wrong one.
              hint:
                summary.activeCap?.uncappedCount > 0
                  ? `excludes ${summary.activeCap.uncappedCount} uncapped`
                  : null,
            },
            {
              label: "Active",
              value: summary.statusCounts?.active ?? 0,
              tone: "success",
              loading,
              icon: <CheckCircle />,
            },
            {
              label: "Closed",
              value: summary.statusCounts?.closed ?? 0,
              tone: "neutral",
              loading,
              icon: <MinusCircle />,
            },
          ]}
        />

        <ListSearchBar
          value={filters.search || ""}
          onChange={(v) => set("search", v)}
          placeholder="Search MID, client, or DBA"
        >
          <select
            className="select w-auto"
            value={filters.status || ""}
            onChange={(e) => set("status", e.target.value)}
            aria-label="Status"
          >
            <option value="">Status: Any</option>
            {Object.entries(MID_STATUS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleExport}
            disabled={exporting || loading || !pagination.total}
            title="Download the current filtered results as CSV"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting…" : "Export"}
          </button>
        </ListSearchBar>

        <RecordTable
          columns={columns}
          rows={mids}
          rowKey={(m) => m.midId}
          // Omitted for processors — rows render non-interactive, and /mids/:id
          // is not admitted for them server-side.
          onRowClick={isProcessorUser ? undefined : (m) => navigate(`/mids/${m.midId}`)}
          loading={loading}
          error={error}
          onRetry={refetch}
          filtered={activeFilterCount > 0}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          noMatch={{ icon: CreditCard, heading: "No matching MIDs" }}
          empty={{
            icon: CreditCard,
            heading: "No MIDs in portfolio",
            description: isPartnerUser
              ? "No MIDs across your attributed clients yet."
              : isProcessorUser
              ? "No MIDs acquired yet."
              : "No MIDs available.",
            errorText: "Failed to load the portfolio.",
            action: isAdmin ? (
              <button className="btn btn-primary" onClick={() => navigate("/mids/create")}>
                <Plus className="h-4 w-4" />
                Create MID
              </button>
            ) : null,
          }}
        />

        <ListPagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 0}
          total={pagination.total || 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      <SearchPanel
        namespace="mids"
        title="Filter MIDs"
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />
    </div>
  );
}

function MidPortfolio() {
  return (
    <SearchProvider namespace="mids">
      <MidPortfolioInner />
    </SearchProvider>
  );
}

export default requireAuth(MidPortfolio);
